import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import { useSearchParams } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import { useAuth } from '../../contexts/AuthContext';
import { getLocalDateInputValue } from '../../utils/date';
import { getCallStatusLabel } from '../../utils/callStatus';
import { callStatusTone, statusColors } from '../../styles/consoleTheme';
import { ConsoleInput, ConsoleLabel } from '../../styles/common/input.styled';
import * as T from '../../styles/common/table.styled';
import PageHeader from '../common/PageHeader';
import PageState from '../common/PageState';
import { toast } from 'react-hot-toast';

const metrics = [
  ['total-calls', '총 통화 건수'], ['success-rate', '통화 성공률'], ['inbound-calls', '수신 통화'],
  ['outbound-calls', '발신 통화'], ['missed-calls', '부재중 통화'], ['average-call-time', '평균 통화 시간'],
];
const formatCallTime = (value: number | string | null | undefined) => {
  if (value === null || value === undefined || value === '') return '-';
  const seconds = Math.round(typeof value === 'string' && value.includes(':')
    ? value.split(':').reduce((total, part) => total * 60 + Number(part), 0)
    : Number(value));
  if (!Number.isFinite(seconds)) return '-';
  const minutes = Math.floor(seconds / 60);
  return minutes > 0 ? `${minutes}분 ${seconds % 60}초` : `${seconds}초`;
};
const Filter = styled.div`display: flex; align-items: flex-end; flex-wrap: wrap; gap: 12px; padding-bottom: 16px; border-bottom: 1px solid ${({ theme }) => theme.color.primary_300};`;
const Field = styled.div`display: flex; flex-direction: column; gap: 6px; input { width: 164px; }`;
const Summary = styled.div`
  display: grid; grid-template-columns: repeat(6, minmax(0, 1fr)); border: 1px solid ${({ theme }) => theme.color.primary_300}; border-radius: 8px; overflow: hidden;
  @media(max-width: 1399px) { grid-template-columns: repeat(3, minmax(0, 1fr)); }
`;
const Metric = styled.div`
  min-width: 0; padding: 18px 16px; border-right: 1px solid ${({ theme }) => theme.color.primary_300};
  &:last-child { border-right: 0; }
  @media(max-width: 1399px) { &:nth-child(3n) { border-right: 0; } &:nth-child(n+4) { border-top: 1px solid ${({ theme }) => theme.color.primary_300}; } }
`;
const MetricLabel = styled.div`font-size: 12px; color: ${({ theme }) => theme.color.text_color_300}; margin-bottom: 8px;`;
const MetricValue = styled.div`
  display: flex; align-items: baseline; flex-wrap: wrap; gap: 4px; font-variant-numeric: tabular-nums;
  strong { font-size: 28px; font-weight: 600; letter-spacing: -0.6px; }
  small { color: ${({ theme }) => theme.color.text_color_300}; font-size: 12px; }
`;
const Grid = styled.div`display: grid; grid-template-columns: minmax(300px, 0.75fr) minmax(0, 1.25fr); gap: 24px; align-items: start; @media(max-width:1399px) { grid-template-columns: 1fr; }`;
const SectionTitle = styled.h2`font-size: 14px; font-weight: 600; margin: 8px 0 16px;`;
const Distribution = styled.div`display: flex; align-items: center; gap: 28px; padding: 24px; border: 1px solid ${({ theme }) => theme.color.primary_300}; border-radius: 8px; flex-wrap: wrap;`;
const Donut = styled.div<{ $gradient: string }>`
  width: 152px; height: 152px; flex-shrink: 0; border-radius: 50%; background: ${({ $gradient }) => $gradient}; display: grid; place-items: center;
`;
const Hole = styled.div`width: 108px; height: 108px; border-radius: 50%; background: ${({ theme }) => theme.color.primary_100}; display: flex; align-items: center; justify-content: center; flex-direction: column; gap: 4px; small { font-size: 11px; color: ${({ theme }) => theme.color.text_color_300}; } strong { font-size: 22px; font-weight: 600; }`;
const Legend = styled.div`display: flex; flex-direction: column; gap: 12px; flex: 1; min-width: 160px;`;
const LegendRow = styled.div`display: flex; align-items: center; gap: 8px; font-size: 12px; span:last-child { margin-left: auto; font-variant-numeric: tabular-nums; color: ${({ theme }) => theme.color.text_color_200}; }`;
const Dot = styled.span<{ $color: string }>`width: 8px; height: 8px; flex-shrink: 0; border-radius: 2px; background: ${({ $color }) => $color};`;
const AgentTable = styled(T.Table)`min-width: 680px; th:nth-child(n+3),td:nth-child(n+3) { text-align: right; }`;

export default function CallStatistics() {
  const [searchParams, setSearchParams] = useSearchParams();
  const today = getLocalDateInputValue();
  const startDate = searchParams.get('start') || getLocalDateInputValue(-30);
  const endDate = searchParams.get('end') || today;
  const { request } = useApi();
  const { mode } = useAuth();
  const theme = useTheme();
  const modeRef = useRef(mode); modeRef.current = mode;
  const sequence = useRef(0);
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [data, setData] = useState<{ metrics: any[]; distribution: any[]; agents: any[] }>({ metrics: [], distribution: [], agents: [] });
  const fetchData = useCallback(async () => {
    const requestId = ++sequence.current;
    const requestMode = mode;
    const current = () => sequence.current === requestId && modeRef.current === requestMode;
    setState('loading');
    try {
      const params = { startDate, endDate };
      const results = await Promise.all([...metrics.map(([endpoint]) => request({ url: `/dashboard/${endpoint}`, params })), request({ url: '/dashboard/distribution', params }), request({ url: '/dashboard/agent-status', params })]);
      if (!current()) return;
      setData({ metrics: results.slice(0, 6), distribution: Array.isArray(results[6]) ? results[6] : [], agents: Array.isArray(results[7]) ? results[7] : [] });
      setState('ready');
    } catch (error) {
      if (!current()) return;
      setState('error');
      toast.error('통계 데이터를 불러오는데 실패했습니다.');
    }
  }, [startDate, endDate, mode, request]);
  useEffect(() => { fetchData(); return () => { sequence.current += 1; }; }, [fetchData]);
  const changeDate = (type: string, value: string) => { const next = new URLSearchParams(searchParams); next.set(type, value); setSearchParams(next); };
  let percent = 0;
  const gradient = data.distribution.map(item => {
    const start = percent; percent += Number(item.percentage) || 0;
    return `${statusColors[theme.name][callStatusTone(item.label)]} ${start}% ${percent}%`;
  }).join(', ');
  return <T.PageContainer>
    <PageHeader title="통화 통계 조회" description="통화 현황과 상담원별 실적을 확인하세요." />
    <Filter>
      <Field><ConsoleLabel htmlFor="stats-start">조회 시작일</ConsoleLabel><ConsoleInput id="stats-start" type="date" value={startDate} onChange={e => changeDate('start', e.target.value)} /></Field>
      <Field><ConsoleLabel htmlFor="stats-end">조회 종료일</ConsoleLabel><ConsoleInput id="stats-end" type="date" value={endDate} onChange={e => changeDate('end', e.target.value)} /></Field>
    </Filter>
    {state !== 'ready' ? <PageState status={state} onRetry={fetchData} /> : <>
      <Summary>{metrics.map(([key, label], index) => {
        const metric = data.metrics[index];
        const value = key === 'average-call-time' && metric?.value !== undefined && metric?.value !== null
          ? formatCallTime(metric.value)
          : metric?.value ?? '—';
        return <Metric key={key}><MetricLabel>{label}</MetricLabel><MetricValue><strong>{value}</strong><small>{key === 'average-call-time' ? '' : metric?.unit || ''}</small></MetricValue></Metric>;
      })}</Summary>
      <Grid><section><SectionTitle>통화 분포</SectionTitle>{data.distribution.length ? <Distribution>
        <Donut $gradient={`conic-gradient(${gradient})`} role="img" aria-label="통화 상태별 분포"><Hole><small>총 통화</small><strong>{data.metrics[0]?.value ?? '—'}</strong></Hole></Donut>
        <Legend>{data.distribution.map((item, index) => <LegendRow key={`${item.label}-${index}`}><Dot $color={statusColors[theme.name][callStatusTone(item.label)]} aria-hidden="true" /><span>{getCallStatusLabel(item.label)}</span><span>{Number(item.percentage).toFixed(2)}% · {item.count}건</span></LegendRow>)}</Legend>
      </Distribution> : <PageState status="empty" title="통화 분포 데이터가 없습니다." />}</section>
      <section><SectionTitle>상담원별 현황</SectionTitle><T.TableArea><AgentTable><colgroup>{[110,80,70,70,70,70,160].map((width,index) => <col key={index} style={{ width }} />)}</colgroup><thead><tr>{['상담원명','내선번호','총 통화','수신','발신','부재중','평균 통화시간'].map(label => <T.Th key={label}>{label}</T.Th>)}</tr></thead><tbody>
        {data.agents.length ? data.agents.map((agent,index) => <T.Tr key={index}><T.Td title={agent.agentName}>{agent.agentName}</T.Td><T.Td>{agent.phone}</T.Td><T.Td>{agent.totalCalls}</T.Td><T.Td>{agent.inboundCalls}</T.Td><T.Td>{agent.outboundCalls}</T.Td><T.Td>{agent.missedCalls}</T.Td><T.Td>{formatCallTime(agent.averageCallTime)}</T.Td></T.Tr>) : <T.Tr><T.Td colSpan={7}><T.NoData>상담원 데이터가 없습니다.</T.NoData></T.Td></T.Tr>}
      </tbody></AgentTable></T.TableArea></section></Grid>
    </>}
  </T.PageContainer>;
}
